import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Menu, X, ChevronDown, ArrowRight, Mail } from 'lucide-react';

import logo from '../assets/images/logo.svg';
import services from '../content/services';
import industries from '../content/industries';
import { navRoutes } from '../content/routes';
import site from '../content/site';
import icons from '../assets/serviceIcons';

/* Which nav items open a panel, and what goes in it. */
const panels = {
  '/services': { items: services, base: '/services', cta: 'View all services' },
  '/industries': { items: industries, base: '/industries', cta: 'View all industries' },
};

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [openPanel, setOpenPanel] = useState(null);
  const [mobileSection, setMobileSection] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const reduce = useReducedMotion();
  const closeTimer = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setOpenPanel(null);
    setMobileSection(null);
  }, [pathname]);

  // Lock the page behind the mobile drawer without the iOS scroll-jump:
  // fix the body at its current offset, then restore it on close.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const y = window.scrollY;
    const { body } = document;
    body.style.position = 'fixed';
    body.style.top = `-${y}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    return () => {
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      window.scrollTo(0, y);
    };
  }, [menuOpen]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      setOpenPanel(null);
      setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const open = (path) => {
    clearTimeout(closeTimer.current);
    setOpenPanel(path);
  };
  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setOpenPanel(null), 140);
  };

  const solid = scrolled || menuOpen;
  const linkTone = solid ? 'text-primary hover:text-secondary' : 'text-white/90 hover:text-white';

  return (
    <>
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        solid ? 'bg-white/90 py-2.5 shadow-soft backdrop-blur-xl md:py-3' : 'bg-transparent py-4 md:py-5'
      }`}
    >
      <div className="container-page flex items-center justify-between gap-4">
        <Link to="/" aria-label="Codelithic Labs, home" className="flex h-11 shrink-0 items-center">
          <img
            src={logo}
            alt="Codelithic Labs Inc."
            width="375"
            height="78"
            className={`w-auto transition-all duration-300 ${
              solid ? 'h-7 sm:h-8 md:h-9' : 'h-8 brightness-0 invert sm:h-9 md:h-10'
            }`}
          />
        </Link>

        {/* Desktop nav — only at xl, because seven items plus a button
            genuinely does not fit at lg on a laptop. */}
        <nav aria-label="Primary" className="hidden items-center gap-7 xl:flex">
          {navRoutes.map((route) => {
            const panel = panels[route.path];
            if (!panel) {
              return (
                <NavLink
                  key={route.path}
                  to={route.path}
                  end={route.path === '/'}
                  className={({ isActive }) =>
                    `text-[0.95rem] font-medium transition-colors ${linkTone} ${isActive ? 'text-secondary' : ''}`
                  }
                >
                  {route.name}
                </NavLink>
              );
            }
            return (
              <div
                key={route.path}
                className="relative"
                onMouseEnter={() => open(route.path)}
                onMouseLeave={scheduleClose}
              >
                <NavLink
                  to={route.path}
                  aria-expanded={openPanel === route.path}
                  aria-haspopup="true"
                  onFocus={() => open(route.path)}
                  className={({ isActive }) =>
                    `inline-flex items-center gap-1 text-[0.95rem] font-medium transition-colors ${linkTone} ${
                      isActive ? 'text-secondary' : ''
                    }`
                  }
                >
                  {route.name}
                  <ChevronDown
                    size={15}
                    aria-hidden="true"
                    className={`transition-transform duration-200 ${openPanel === route.path ? 'rotate-180' : ''}`}
                  />
                </NavLink>

                <AnimatePresence>
                  {openPanel === route.path && (
                    <motion.div
                      initial={{ opacity: 0, y: reduce ? 0 : 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: reduce ? 0 : 8 }}
                      transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
                      className="absolute left-1/2 top-full w-[36rem] -translate-x-1/2 pt-4"
                    >
                      <div className="rounded-3xl bg-white p-3 shadow-lift ring-1 ring-primary/5">
                        <div className="grid grid-cols-2 gap-1">
                          {panel.items.map((it) => (
                            <Link
                              key={it.slug}
                              to={`${panel.base}/${it.slug}`}
                              className="group flex items-center gap-3 rounded-2xl p-3 transition-colors hover:bg-light"
                            >
                              <img
                                src={icons[it.icon]}
                                alt=""
                                aria-hidden="true"
                                width="64"
                                height="64"
                                loading="lazy"
                                className="h-9 w-9 shrink-0 object-contain"
                              />
                              <span className="min-w-0">
                                <span className="block text-sm font-semibold text-primary transition-colors group-hover:text-secondary">
                                  {it.name}
                                </span>
                                <span className="block truncate text-xs text-accent">{it.tagline}</span>
                              </span>
                            </Link>
                          ))}
                        </div>
                        <Link
                          to={panel.base}
                          className="mt-2 flex items-center justify-center gap-1.5 rounded-2xl bg-light py-3 text-sm font-semibold text-primary transition-colors hover:bg-primary hover:text-white"
                        >
                          {panel.cta}
                          <ArrowRight size={15} aria-hidden="true" />
                        </Link>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </nav>

        <Link to="/contact" className="btn-primary btn-md hidden xl:inline-flex">
          Get started
        </Link>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
          /* 44px minimum touch target */
          className={`-mr-2 grid h-11 w-11 place-items-center rounded-xl transition-colors xl:hidden ${
            solid ? 'text-primary' : 'text-white'
          }`}
        >
          {menuOpen ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

    </header>

    {/* ---------------- Mobile drawer ----------------
          Deliberately a SIBLING of <header>, not a child. The header carries
          backdrop-blur when solid, and an element with backdrop-filter becomes
          the containing block for its position:fixed descendants — which sized
          this drawer against the ~64px bar instead of the viewport and
          collapsed it to nothing. Outside the header it resolves against the
          viewport as intended. */}
    <AnimatePresence>
      {menuOpen && (
        <motion.div
          initial={{ opacity: 0, y: reduce ? 0 : -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: reduce ? 0 : -8 }}
          transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
          /* Sits below the bar and owns its own scroll, so long lists
             work on short phones without fighting the locked body. */
          className="fixed inset-0 z-40 overflow-y-auto overscroll-contain bg-white pt-[var(--header-h)] xl:hidden"
        >
          <nav aria-label="Mobile" className="container-page flex min-h-full flex-col py-5">
            <ul className="divide-y divide-primary/[0.07]">
              {navRoutes.map((route) => {
                const panel = panels[route.path];
                const expanded = mobileSection === route.path;
                return (
                  <li key={route.path}>
                    {panel ? (
                      <>
                        <div className="flex items-center">
                          <NavLink
                            to={route.path}
                            className="flex-grow py-4 font-display text-xl font-bold text-primary"
                          >
                            {route.name}
                          </NavLink>
                          <button
                            type="button"
                            onClick={() => setMobileSection(expanded ? null : route.path)}
                            aria-expanded={expanded}
                            aria-label={`${expanded ? 'Hide' : 'Show'} all ${route.name.toLowerCase()}`}
                            className="grid h-11 w-11 place-items-center rounded-xl text-primary active:bg-light"
                          >
                            <ChevronDown
                              size={20}
                              className={`transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
                            />
                          </button>
                        </div>
                        <AnimatePresence initial={false}>
                          {expanded && (
                            <motion.ul
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                              className="overflow-hidden"
                            >
                              {panel.items.map((it) => (
                                <li key={it.slug}>
                                  <Link
                                    to={`${panel.base}/${it.slug}`}
                                    className="flex items-center gap-3 rounded-xl py-2.5 pl-3 pr-2 text-[0.95rem] font-medium text-accent active:bg-light"
                                  >
                                    <img
                                      src={icons[it.icon]}
                                      alt=""
                                      aria-hidden="true"
                                      width="64"
                                      height="64"
                                      loading="lazy"
                                      className="h-7 w-7 shrink-0 object-contain"
                                    />
                                    {it.name}
                                  </Link>
                                </li>
                              ))}
                              <li className="pb-3 pl-3 pt-1.5">
                                <Link to={panel.base} className="text-sm font-semibold text-secondary">
                                  {panel.cta} →
                                </Link>
                              </li>
                            </motion.ul>
                          )}
                        </AnimatePresence>
                      </>
                    ) : (
                      <NavLink
                        to={route.path}
                        end={route.path === '/'}
                        className="block py-4 font-display text-xl font-bold text-primary"
                      >
                        {route.name}
                      </NavLink>
                    )}
                  </li>
                );
              })}
            </ul>

            <div className="mt-auto pt-8">
              <Link to="/contact" className="btn-primary btn-lg w-full">
                Get started
              </Link>
              <a
                href={`mailto:${site.email}`}
                className="mt-5 flex items-center justify-center gap-2 pb-2 text-sm font-medium text-accent"
              >
                <Mail size={15} aria-hidden="true" />
                {site.email}
              </a>
            </div>
          </nav>
        </motion.div>
      )}
    </AnimatePresence>
    </>
  );
};

export default Header;
